import { UserProvider as Auth0Provider, useUser as useAuth0 } from "@auth0/nextjs-auth0/client";
import { useRouter } from "next/router";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { loginPath } from "~/utils/paths";

export type AuthContextProps = {
  email?: string;
  isLoading: boolean;
  login: () => void;
};

export const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

/**
 *
 *  AuthProvider is the authentication provider for our app, responsible for the
 *  functionality exposed by the `useAuth` hook.
 *
 *  e.g. login/signup/logout, the authenticated email address, loading states
 *
 */
const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { user: auth0User, isLoading: auth0Loading } = useAuth0();
  const router = useRouter();
  const [email, setEmail] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const login = useCallback(() => {
    void router.push(loginPath(router.asPath));
  }, [router]);

  useEffect(() => {
    if (!auth0Loading && !!auth0User) {
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      setEmail(auth0User.email!);
    }
  }, [auth0Loading, auth0User]);

  // Handle internal loading state when user is not authenticated
  useEffect(() => {
    const loadedAndUnathenticated = !auth0Loading && !auth0User;
    if (loadedAndUnathenticated) setIsLoading(false);
  }, [auth0Loading, auth0User]);

  const memoisedValue = useMemo(
    () => ({
      email,
      isLoading,
      login,
    }),
    [isLoading, login, email],
  );

  return <AuthContext.Provider value={memoisedValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

/**
 *
 *  OneAuthProviderToRuleThemAll provides a convenience wrapper for composing all of the
 *  authentication providers together. It doesn't hold any functionality, it's really just a quirk of
 *  needing to initialize providers before their consuming `use[Stuff]` hooks can be called.
 *
 */
export const OneAuthProviderToRuleThemAll = ({
  children,
}: {
  children: ReactNode;
}) => {
  return (
    <Auth0Provider>
      <AuthProvider>{children}</AuthProvider>
    </Auth0Provider>
  );
};
